import React, { useState } from "react";
import { BookOpen, Search, ArrowRight, Clock, Calendar, Sparkles, Filter } from "lucide-react";
import { getAllGuides, GuideArticle } from "../../utils/guideRegistry";
import { useSeoMetadata } from "../PublicPages";
import { SeoBreadcrumbs } from "./SeoBreadcrumbs";
import { SeoRelatedLinks } from "./SeoRelatedLinks";

interface GuideHubPageProps {
  onNavigate: (path: string) => void;
  onLogin?: (isSignUp?: boolean) => void;
  currentUser?: any;
}

export function GuideHubPage({ onNavigate, onLogin = () => {}, currentUser }: GuideHubPageProps) {
  const guides = getAllGuides();
  const [selectedCategory, setSelectedCategory] = useState<string>("All");

  useSeoMetadata({
    title: "Career & Direct Outreach Guide Hub | BizNearYou",
    description: "Actionable guides for local job searching, cold emailing small business owners, landing entry-level jobs without experience, and finding hidden opportunities.",
    canonicalUrl: "https://getbiznearyou.com/guides",
    schemaJson: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Local Job Search & Direct Outreach Guide Directory",
      "itemListElement": guides.map((g, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "name": g.h1,
        "url": `https://getbiznearyou.com/guides/${g.slug}`
      }))
    }
  });

  const categories = [
    "All", 
    "Recruiting & Staffing", 
    "Career Support", 
    "Business Prospecting", 
    "Employer Research", 
    "Job Search Strategy", 
    "Direct Outreach", 
    "Entry Level", 
    "Internships", 
    "Safety & Research"
  ];

  const filteredGuides = selectedCategory === "All"
    ? guides
    : guides.filter((g) => g.category === selectedCategory);

  const handleLinkClick = (e: React.MouseEvent, path: string) => {
    e.preventDefault();
    onNavigate(path);
  };

  return (
    <div className="w-full">
      <main className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <SeoBreadcrumbs items={[{ label: "Guides", path: "/guides" }]} onNavigate={onNavigate} />

        {/* Hero Banner */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 mb-8 shadow-xs relative overflow-hidden">
          <div className="absolute top-0 right-0 -mt-10 -mr-10 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative z-10 max-w-3xl space-y-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-indigo-50 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Actionable Job Search Playbooks</span>
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold text-slate-950 dark:text-white tracking-tight leading-tight">
              Career & Direct Outreach Guide Hub
            </h1>
            <p className="text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
              Master the art of pitching local business owners, unlocking the hidden job market, writing entry-level resumes, and landing rewarding careers near home.
            </p>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex items-center gap-2 overflow-x-auto pb-4 mb-8 no-scrollbar">
          <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition cursor-pointer ${
                selectedCategory === cat
                  ? "bg-indigo-600 text-white shadow-xs"
                  : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Guide Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {filteredGuides.map((guide) => (
            <article
              key={guide.slug}
              className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 hover:shadow-md hover:border-indigo-300 dark:hover:border-indigo-600 transition flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between gap-2 mb-3">
                  <span className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-900/50">
                    {guide.category}
                  </span>
                  <div className="flex items-center gap-1 text-[11px] text-slate-400 dark:text-slate-500">
                    <Clock className="w-3 h-3" />
                    <span>{guide.estimatedReadTime}</span>
                  </div>
                </div>

                <h2 className="text-base font-bold text-slate-900 dark:text-white mb-2 leading-snug">
                  <a
                    href={`/guides/${guide.slug}`}
                    onClick={(e) => handleLinkClick(e, `/guides/${guide.slug}`)}
                    className="hover:text-indigo-600 dark:hover:text-indigo-400 transition cursor-pointer"
                  >
                    {guide.h1}
                  </a>
                </h2>

                <p className="text-xs text-slate-600 leading-relaxed mb-4 line-clamp-3">
                  {guide.directAnswer}
                </p>
              </div>

              <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[11px] text-slate-400 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>Updated {guide.lastUpdatedDate}</span>
                </span>

                <a
                  href={`/guides/${guide.slug}`}
                  onClick={(e) => handleLinkClick(e, `/guides/${guide.slug}`)}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 cursor-pointer"
                >
                  <span>Read Guide</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </article>
          ))}
        </div>

        <SeoRelatedLinks currentRoute="/guides" onNavigate={onNavigate} />
      </main>
    </div>
  );
}
